import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Warning,
  Delete,
  Storage,
  Language,
  Notifications,
  Rule
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

// ============================================================================
// Types
// ============================================================================

export interface DeleteConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  itemName: string;
  itemType: 'exporter' | 'website' | 'channel' | 'rule';
  loading?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

const ITEM_TYPE_CONFIG = {
  exporter: {
    icon: Storage,
    color: 'primary' as const,
    warning: 'This will remove the exporter from Prometheus monitoring. This will also remove all alerts and notification subscriptions for this exporter. Historical metrics data will be preserved but no new data will be collected.',
    consequences: [
      'Stop collecting metrics from this target',
      'Remove from Prometheus scrape configuration',
      'Delete all alert rule associations for this exporter',
      'Remove all notification channel subscriptions for this exporter',
      'Keep historical data in Prometheus for the retention period'
    ]
  },
  website: {
    icon: Language,
    color: 'info' as const,
    warning: 'This will stop monitoring this website. Historical uptime and response data will be preserved.',
    consequences: [
      'Stop checking website availability',
      'Remove from Blackbox Exporter configuration',
      'Disable associated alerts',
      'Keep historical data in Prometheus'
    ]
  },
  channel: {
    icon: Notifications,
    color: 'secondary' as const,
    warning: 'This will delete the notification channel. You will no longer receive alerts through this channel.',
    consequences: [
      'Stop sending alerts to this channel',
      'Remove all exporter subscriptions',
      'Cannot be undone - channel must be recreated'
    ]
  },
  rule: {
    icon: Rule,
    color: 'warning' as const,
    warning: 'This will delete this custom alert rule and remove it from Prometheus monitoring on the next sync.',
    consequences: [
      'Remove the alert rule from monitoring',
      'Stop generating alerts for this condition',
      'Existing alert history is preserved',
    ]
  }
};

// ============================================================================
// Component
// ============================================================================

export const DeleteConfirmationDialog: React.FC<DeleteConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  itemName,
  itemType,
  loading = false
}) => {
  const theme = useTheme();
  const config = ITEM_TYPE_CONFIG[itemType];
  const ItemIcon = config.icon;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderTop: `4px solid ${theme.palette.error.main}`
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Warning sx={{ mr: 1, color: theme.palette.warning.main }} />
          {title}
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 1 }}>
          {/* Item being deleted */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 2,
              bgcolor: theme.palette.action.hover,
              borderRadius: 1,
              mb: 3
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 48,
                height: 48,
                borderRadius: '50%',
                bgcolor: theme.palette[config.color].light + '30',
                mr: 2
              }}
            >
              <ItemIcon sx={{ color: theme.palette[config.color].main }} />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                {itemType.charAt(0).toUpperCase() + itemType.slice(1)}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                {itemName}
              </Typography>
            </Box>
          </Box>

          {/* Warning message */}
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              {config.warning}
            </Typography>
          </Alert>

          {/* Consequences list */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            What will happen:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 3 }}>
            {config.consequences.map((consequence, index) => (
              <Typography
                key={index}
                component="li"
                variant="body2"
                color="text.secondary"
                sx={{ mb: 0.5 }}
              >
                {consequence}
              </Typography>
            ))}
          </Box>

          {/* Final confirmation text */}
          <Typography variant="body2" sx={{ mt: 3, fontWeight: 'medium' }}>
            Are you sure you want to delete this {itemType}?
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="error"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <Delete />}
        >
          {loading ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteConfirmationDialog;
